/**
 * Chance-event table (HEX-137 / ADR-0023).
 *
 * Replaces the old roll-or-sacrifice trap with a weighted table of short
 * risk/reward beats. Stepping a (hidden-until-crossed, HEX-85) danger tile fires
 * exactly one event, picked deterministically from the seeded RNG stream the
 * host derives from `(coords, crossings)` — the same nonce the trap roll used,
 * so a re-cross re-rolls but a given `(tile, crossing)` is replayable.
 *
 * The table is pure data so events can be added without touching `EventFlow`.
 * The per-event maths live here too (gamble payout, cache value, cache-reward
 * variant) so they unit-test without a DOM.
 */

import { GAMEPLAY_CARDS } from '../../combat/cardDrops';
import type { DieType, Rng } from '../../combat/dice';
import { SPECIALIST_DICE } from '../../combat/diceRoster';
import type { Card, Rarity } from '../../combat/types';

/** The starter event set (ADR-0023). Breadth is a content follow-up. */
export type ChanceEventKind = 'gamble' | 'ambush' | 'cache' | 'quiet';

export interface ChanceEventDef {
  readonly kind: ChanceEventKind;
  /** Relative weight in the table; need not sum to any particular total. */
  readonly weight: number;
  /** Modal title for the event beat. */
  readonly title: string;
}

/**
 * Starter table (4 events). Gamble + cache are the common reward/risk beats;
 * ambush is the rarer teeth; quiet is the "nothing happens" pressure valve that
 * keeps the table from reading as a slot machine.
 */
export const CHANCE_EVENT_TABLE: readonly ChanceEventDef[] = [
  { kind: 'gamble', weight: 3, title: 'A wager' },
  { kind: 'ambush', weight: 2, title: 'Ambush!' },
  { kind: 'cache', weight: 3, title: 'A hidden cache' },
  { kind: 'quiet', weight: 2, title: 'All quiet' },
];

/**
 * Pick an event from the weighted table given a normalised roll in `[0, 1)`
 * (one `Rng()` draw). Deterministic: the same roll against the same table always
 * lands on the same event. A roll at or beyond the total (defensive) falls to
 * the last entry.
 */
export function pickChanceEvent(
  roll: number,
  table: readonly ChanceEventDef[] = CHANCE_EVENT_TABLE,
): ChanceEventDef {
  return weightedPick(roll, table, (e) => e.weight);
}

/**
 * Weighted pick over `items`: spread a normalised `roll` in `[0, 1)` across the
 * summed weights and return the entry whose band it lands in. A roll at or
 * beyond the total (defensive) falls to the last entry. Deterministic.
 */
function weightedPick<T>(roll: number, items: readonly T[], weightOf: (item: T) => number): T {
  const total = items.reduce((sum, item) => sum + weightOf(item), 0);
  let acc = roll * total;
  for (const item of items) {
    acc -= weightOf(item);
    if (acc < 0) return item;
  }
  return items[items.length - 1] as T;
}

// --- Gamble -----------------------------------------------------------------

/** Gold staked on a gamble, capped at what the player can afford. */
export const GAMBLE_BASE_STAKE = 10;

/** The wager offered: the base stake, or all the player's gold if poorer. */
export function gambleStake(gold: number): number {
  return Math.min(gold, GAMBLE_BASE_STAKE);
}

export interface GambleResult {
  readonly won: boolean;
  /** Net gold change: `+stake` on a win (double-or-nothing), `-stake` on a loss. */
  readonly delta: number;
}

/**
 * Resolve a gamble from a d6 face: high (4–6) doubles the stake (net `+stake`),
 * low (1–3) forfeits it (net `-stake`). Straight 50/50 double-or-nothing.
 */
export function resolveGamble(face: number, stake: number): GambleResult {
  const won = face >= 4;
  return { won, delta: won ? stake : -stake };
}

// --- Cache ------------------------------------------------------------------

/** Base gold in a cache before the danger-tier bonus. */
export const CACHE_BASE_GOLD = 15;

/** Per-tier gold step added to a cache. */
export const CACHE_TIER_STEP = 5;

/**
 * Gold in a cache, scaled by the tile's danger tier (1–5): deeper frontier
 * caches pay more.
 */
export function cacheGold(tier: number): number {
  return CACHE_BASE_GOLD + (tier - 1) * CACHE_TIER_STEP;
}

// --- Cache reward variant (HEX-141 / ADR-0023) ------------------------------

/**
 * What a cache yields. Now that the ADR-0022 inventory rework (HEX-138) landed
 * place-or-decline, a cache can hold a card or a specialist die, not just gold —
 * filling the seam HEX-137 left in `EventFlow.renderCache`.
 */
export type CacheReward = 'gold' | 'card' | 'die';

export interface CacheRewardDef {
  readonly kind: CacheReward;
  /** Relative weight in the table; need not sum to any particular total. */
  readonly weight: number;
}

/**
 * Weighted cache-reward table. Gold stays the most common beat; a card (offered
 * place-or-decline, ADR-0022) is the mid reward; a specialist die is the rarest.
 */
export const CACHE_REWARD_TABLE: readonly CacheRewardDef[] = [
  { kind: 'gold', weight: 5 },
  { kind: 'card', weight: 3 },
  { kind: 'die', weight: 2 },
];

/** Pick a cache reward kind from a normalised roll in `[0, 1)` (one `Rng()`
 * draw). Deterministic, mirroring `pickChanceEvent`. */
export function pickCacheReward(
  roll: number,
  table: readonly CacheRewardDef[] = CACHE_REWARD_TABLE,
): CacheReward {
  return weightedPick(roll, table, (e) => e.weight).kind;
}

const RARITY_RANK: Record<Rarity, number> = { common: 0, rare: 1, epic: 2, legendary: 3 };

/** Rarity ceiling for a cache card by danger tier (1–5): deeper caches can hold
 * rarer cards. Tier ≤2 → common, 3 → rare, 4 → epic, 5 → legendary. */
export function cacheCardCeiling(tier: number): Rarity {
  if (tier >= 5) return 'legendary';
  if (tier === 4) return 'epic';
  if (tier === 3) return 'rare';
  return 'common';
}

/**
 * Pick a cache card: uniformly from the §8 gameplay pool whose rarity is at or
 * below the tier ceiling. Always returns a card (the common pool is never
 * empty). Consumes one `rng()` draw. The host then offers it place-or-decline.
 */
export function pickCacheCard(tier: number, rng: Rng): Card {
  const ceiling = RARITY_RANK[cacheCardCeiling(tier)];
  const pool = GAMEPLAY_CARDS.filter((c) => RARITY_RANK[c.rarity] <= ceiling);
  const source = pool.length > 0 ? pool : GAMEPLAY_CARDS;
  const index = Math.min(source.length - 1, Math.floor(rng() * source.length));
  return source[index] as Card;
}

/** Pick a specialist die for a cache: uniformly over the roster. Always returns
 * a die. Consumes one `rng()` draw. The host clones + appends it to the pool. */
export function pickCacheDie(rng: Rng): DieType {
  const index = Math.min(SPECIALIST_DICE.length - 1, Math.floor(rng() * SPECIALIST_DICE.length));
  return SPECIALIST_DICE[index] as DieType;
}

// --- Ambush -----------------------------------------------------------------

/**
 * Safe-zone distance fed to the latent-enemy picker for an ambush. Pinned to the
 * spawn band so the ambusher is a weak, below-local-band foe wherever it springs
 * (ADR-0023: "a weak (low-tier) combat encounter, below the local band").
 */
export const AMBUSH_DISTANCE = 0;

// --- Quiet ------------------------------------------------------------------

/** Flavour lines for a quiet event; one is picked from the RNG stream. */
export const QUIET_FLAVOURS: readonly string[] = [
  'A twig snaps somewhere in the dark — then nothing. The frontier holds its breath.',
  'You freeze, certain something stirred. The moment passes. Just the wind.',
  'A cold draught, a prickle at your neck — and then only the quiet of empty ground.',
];

/** Pick a quiet flavour line from a normalised roll in `[0, 1)`. */
export function pickQuietFlavour(roll: number): string {
  const i = Math.min(QUIET_FLAVOURS.length - 1, Math.floor(roll * QUIET_FLAVOURS.length));
  return QUIET_FLAVOURS[i] as string;
}
